import { onUnmounted, ref, type Ref } from "vue";
import type { TransactionStatusResponse } from "@evka/shared";
import { ApiError, getTransactionStatus, reconcileTransaction } from "../api";

export interface UseTransactionStatus {
  status: Ref<TransactionStatusResponse | null>;
  loading: Ref<boolean>;
  error: Ref<string | null>;
  errorCode: Ref<string | null>;
  timedOut: Ref<boolean>;
  start: (transactionId: string, token: string) => void;
  stop: () => void;
}

interface PollOptions {
  intervalMs?: number;
  maxIntervalMs?: number;
  totalTimeoutMs?: number;
}

const DEFAULT_OPTIONS: Required<PollOptions> = {
  intervalMs: 2000,
  maxIntervalMs: 8000,
  totalTimeoutMs: 60_000,
};

/**
 * Drives the /return page: on mount, reads the current status from the
 * backend and — if still pending — asks the backend to reconcile once
 * with SumUp. That reconcile is the only way to tell what happened
 * after "Back to merchant", since SumUp's redirect URL carries no
 * status. Falls through to timer-based polling afterwards for the case
 * where reconcile itself returned still-pending (SumUp was slow / open).
 */
export function useTransactionStatus(opts: PollOptions = {}): UseTransactionStatus {
  const cfg = { ...DEFAULT_OPTIONS, ...opts };

  const status = ref<TransactionStatusResponse | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const errorCode = ref<string | null>(null);
  const timedOut = ref(false);

  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;
  let startedAt = 0;
  let currentInterval = cfg.intervalMs;

  function clearTimer() {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  }

  function stop() {
    stopped = true;
    clearTimer();
    loading.value = false;
  }

  function recordError(err: unknown) {
    if (err instanceof ApiError) {
      errorCode.value = err.code;
      error.value = err.message;
    } else {
      errorCode.value = "network_error";
      error.value = err instanceof Error ? err.message : String(err);
    }
  }

  async function initialCheck(transactionId: string, token: string) {
    if (stopped) return;
    let next: TransactionStatusResponse | null = null;
    try {
      next = await getTransactionStatus(transactionId, token);
      status.value = next;
    } catch (err) {
      recordError(err);
    }

    if (next && next.status !== "pending") {
      stop();
      return;
    }

    // Still pending — ask the backend to reconcile with SumUp once.
    if (!stopped) {
      try {
        const reconciled = await reconcileTransaction(transactionId, token);
        status.value = reconciled;
        if (reconciled.status !== "pending") {
          stop();
          return;
        }
      } catch (err) {
        // Reconcile failure isn't fatal — fall back to polling.
        recordError(err);
      }
    }

    // Schedule the first timer-based poll tick.
    scheduleNext(transactionId, token);
  }

  function scheduleNext(transactionId: string, token: string) {
    if (stopped) return;
    const elapsed = Date.now() - startedAt;
    if (elapsed >= cfg.totalTimeoutMs) {
      timedOut.value = true;
      stop();
      return;
    }
    currentInterval = Math.min(Math.round(currentInterval * 1.4), cfg.maxIntervalMs);
    const remaining = cfg.totalTimeoutMs - elapsed;
    const wait = Math.max(250, Math.min(currentInterval, remaining));
    timer = setTimeout(() => void tick(transactionId, token), wait);
  }

  async function tick(transactionId: string, token: string) {
    if (stopped) return;
    try {
      const next = await getTransactionStatus(transactionId, token);
      status.value = next;
      if (next.status !== "pending") {
        stop();
        return;
      }
    } catch (err) {
      recordError(err);
      // Don't stop on a transient error — keep polling until the timeout.
    }
    scheduleNext(transactionId, token);
  }

  function start(transactionId: string, token: string) {
    stopped = false;
    timedOut.value = false;
    error.value = null;
    errorCode.value = null;
    status.value = null;
    loading.value = true;
    startedAt = Date.now();
    currentInterval = cfg.intervalMs;
    void initialCheck(transactionId, token);
  }

  onUnmounted(stop);

  return { status, loading, error, errorCode, timedOut, start, stop };
}
